import { useState, useEffect } from 'react';
import {
  MousePointerClick, Plus, Trash2, Copy, Check, ChevronDown, ChevronUp,
  AlertCircle, X,
} from 'lucide-react';
import Layout from '../components/Layout';
import TagCreator from '../components/TagCreator';
import { PageHeader, Button, Badge, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { buttonsApi, platformConnectionsApi } from '../lib/api';
import type { ButtonConfig, ButtonAction, ButtonClick } from '../types/buttons';
import type { PlatformConnection } from '../types/vault';

export default function Buttons() {
  const [configs, setConfigs] = useState<ButtonConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [newName, setNewName] = useState('');
  const [newConnId, setNewConnId] = useState('');
  const [creating, setCreating] = useState(false);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<ButtonConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);
  const [cfTags, setCfTags] = useState<Array<{ id: number; name: string; color: string }>>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [clicks, setClicks] = useState<ButtonClick[]>([]);
  const [loadingClicks, setLoadingClicks] = useState(false);

  const loadConfigs = async () => {
    try {
      const res = await buttonsApi.list();
      if (res.success && res.data) {
        setConfigs((res.data as unknown as { configs: ButtonConfig[] }).configs || []);
      }
    } catch {
      setError('Failed to load button configs');
    } finally {
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    try {
      const res = await platformConnectionsApi.list();
      if (res.success && res.data) {
        const all = (res.data as unknown as { connections: PlatformConnection[] }).connections || [];
        setConnections(all.filter((c) => c.platform === 'clickfunnels' && c.status === 'active'));
      }
    } catch {}
  };

  useEffect(() => {
    loadConfigs();
    loadConnections();
  }, []);

  const handleCreate = async () => {
    if (!newName.trim() || !newConnId) return;
    setCreating(true);
    try {
      const res = await buttonsApi.create(newName.trim(), newConnId);
      if (res.success) {
        setShowCreateModal(false);
        setNewName('');
        setNewConnId('');
        loadConfigs();
      } else {
        setError(res.message || 'Failed to create');
      }
    } catch {
      setError('Failed to create button config');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this button config?')) return;
    try {
      await buttonsApi.delete(id);
      setConfigs((prev) => prev.filter((c) => c.id !== id));
      if (expandedId === id) {
        setExpandedId(null);
        setEditConfig(null);
      }
    } catch {
      setError('Failed to delete');
    }
  };

  const handleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setEditConfig(null);
      return;
    }
    setExpandedId(id);
    try {
      const res = await buttonsApi.get(id);
      if (res.success && res.data) {
        setEditConfig((res.data as unknown as { config: ButtonConfig }).config);
      }
    } catch {
      setError('Failed to load button config');
    }
    loadTags(id);
    loadClicks(id);
  };

  const handleSave = async () => {
    if (!editConfig) return;
    setSaving(true);
    try {
      const res = await buttonsApi.update(editConfig.id, {
        name: editConfig.name,
        buttonActions: editConfig.buttonActions,
        styleConfig: editConfig.styleConfig,
      });
      if (res.success && res.data) {
        const updated = (res.data as unknown as { config: ButtonConfig }).config;
        setEditConfig(updated);
        setConfigs((prev) => prev.map((c) => (c.id === updated.id ? { ...c, name: updated.name } : c)));
      }
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (config: ButtonConfig) => {
    const newStatus = config.status === 'published' ? 'draft' : 'published';
    try {
      const res = await buttonsApi.update(config.id, { status: newStatus });
      if (res.success) {
        loadConfigs();
        if (editConfig?.id === config.id) {
          setEditConfig((prev) => prev ? { ...prev, status: newStatus } : prev);
        }
      }
    } catch {
      setError('Failed to update status');
    }
  };

  const loadTags = async (id: string) => {
    setLoadingTags(true);
    try {
      const res = await buttonsApi.getTags(id);
      if (res.success && res.data) {
        setCfTags((res.data as unknown as { tags: Array<{ id: number; name: string; color: string }> }).tags || []);
      }
    } catch {}
    setLoadingTags(false);
  };

  const loadClicks = async (id: string) => {
    setLoadingClicks(true);
    try {
      const res = await buttonsApi.getClicks(id);
      if (res.success && res.data) {
        setClicks((res.data as unknown as { clicks: ButtonClick[] }).clicks || []);
      }
    } catch {}
    setLoadingClicks(false);
  };

  const addAction = () => {
    if (!editConfig) return;
    const newAction: ButtonAction = {
      id: crypto.randomUUID(),
      actionName: '',
      tagIds: [],
      label: '',
    };
    setEditConfig({
      ...editConfig,
      buttonActions: [...editConfig.buttonActions, newAction],
    });
  };

  const updateAction = (actionId: string, updates: Partial<ButtonAction>) => {
    if (!editConfig) return;
    setEditConfig({
      ...editConfig,
      buttonActions: editConfig.buttonActions.map((a) =>
        a.id === actionId ? { ...a, ...updates } : a
      ),
    });
  };

  const removeAction = (actionId: string) => {
    if (!editConfig) return;
    setEditConfig({
      ...editConfig,
      buttonActions: editConfig.buttonActions.filter((a) => a.id !== actionId),
    });
  };

  function timeAgo(ts: number): string {
    const seconds = Math.floor((Date.now() - ts) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <PageHeader
          eyebrow="Lead capture"
          title="Button Tracking"
          description="Track button clicks on your pages and tag contacts in ClickFunnels"
          actions={<Button icon={Plus} onClick={() => setShowCreateModal(true)}>New Button Config</Button>}
        />

        {error && (
          <div className="mb-4 p-3 bg-danger-soft border border-line rounded-lg flex items-center gap-2 text-danger text-sm">
            <AlertCircle className="w-4 h-4" /> <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-auto text-ink-3 hover:text-danger"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Info banner */}
        <div className="mb-6 bg-accent-soft border border-line rounded-lg p-4 text-sm text-ink-2">
          <p className="font-medium mb-1">How it works</p>
          <p>
            Add your ClickFunnels page buttons as actions using their <code className="bg-surface-2 text-ink font-mono px-1 rounded">#hash</code> links.
            When a visitor clicks a tracked button, their email is read from the page (via Garlic localStorage).
            If no email is found, a popup captures it. The contact is then tagged in ClickFunnels with the tags you configure.
          </p>
        </div>

        {/* Config list */}
        {configs.length === 0 ? (
          <EmptyState
            icon={MousePointerClick}
            title="No button configs yet"
            description="Create your first one to start tracking clicks."
            action={<Button icon={Plus} onClick={() => setShowCreateModal(true)}>New Button Config</Button>}
          />
        ) : (
          <div className="space-y-3">
            {configs.map((config) => (
              <div key={config.id} className="ds-card overflow-hidden hover:-translate-y-0.5 hover:shadow-card transition-all">
                {/* Row */}
                <div
                  className="flex items-center px-3 md:px-5 py-3 md:py-4 cursor-pointer hover:bg-surface-2"
                  onClick={() => handleExpand(config.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                      <span className="font-medium text-ink truncate">{config.name}</span>
                      <Badge tone={config.status === 'published' ? 'success' : 'neutral'}>{config.status}</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-ink-3">
                      <span className="truncate font-mono">/b/{config.slug}</span>
                      {config.connectionName && <span className="hidden sm:inline">via {config.connectionName}</span>}
                      <span className="font-mono">{config.clickCount || 0} clicks</span>
                      <span>{timeAgo(config.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleTogglePublish(config); }}
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                      style={{ background: config.status === 'published' ? 'var(--success)' : 'var(--ink-3)' }}
                      title={config.status === 'published' ? 'Unpublish' : 'Publish'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        config.status === 'published' ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(config.id); }}
                      className="p-1.5 text-ink-3 hover:text-danger"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandedId === config.id ? <ChevronUp className="w-4 h-4 text-ink-3" /> : <ChevronDown className="w-4 h-4 text-ink-3" />}
                  </div>
                </div>

                {/* Expanded editor */}
                {expandedId === config.id && editConfig && (
                  <div className="border-t border-line p-4 md:p-6">
                    {/* Config name */}
                    <div className="mb-6">
                      <label className="ds-label">Config Name</label>
                      <input
                        type="text"
                        value={editConfig.name}
                        onChange={(e) => setEditConfig({ ...editConfig, name: e.target.value })}
                        className="ds-input w-full max-w-md"
                      />
                    </div>

                    {/* Button Actions */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-sm font-semibold text-ink-2">Button Actions</h3>
                        <Button variant="primary" icon={Plus} onClick={addAction} className="!h-8 text-xs">
                          Add Action
                        </Button>
                      </div>

                      {editConfig.buttonActions.length === 0 ? (
                        <div className="text-center py-8 border border-dashed border-line rounded-lg">
                          <p className="text-ink-3 text-sm">No button actions configured yet.</p>
                          <p className="text-ink-3 text-xs mt-1">Add an action to start tracking button clicks.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {editConfig.buttonActions.map((action, idx) => (
                            <div key={action.id} className="border border-line rounded-lg p-4 bg-surface-2">
                              <div className="flex items-start justify-between mb-3">
                                <span className="text-xs font-medium text-ink-3">Action #{idx + 1}</span>
                                <button
                                  onClick={() => removeAction(action.id)}
                                  className="text-ink-3 hover:text-danger"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="ds-label">Label</label>
                                  <input
                                    type="text"
                                    value={action.label}
                                    onChange={(e) => updateAction(action.id, { label: e.target.value })}
                                    placeholder="e.g. Buy Now Button"
                                    className="ds-input w-full"
                                  />
                                </div>
                                <div>
                                  <label className="ds-label">Action Hash (without #)</label>
                                  <div className="flex items-center">
                                    <span className="text-ink-3 text-sm mr-1">#</span>
                                    <input
                                      type="text"
                                      value={action.actionName}
                                      onChange={(e) => {
                                        const val = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                                        updateAction(action.id, { actionName: val });
                                      }}
                                      placeholder="buy-now"
                                      className="ds-input flex-1 font-mono"
                                    />
                                  </div>
                                  <p className="text-xs text-ink-3 mt-1">Matches buttons with href="#{action.actionName || '...'}" on your page</p>
                                </div>
                              </div>

                              {/* Tag selection */}
                              <div className="mt-4">
                                <label className="ds-label">Tags to Apply on Click</label>
                                {loadingTags ? (
                                  <p className="text-xs text-ink-3">Loading tags...</p>
                                ) : cfTags.length === 0 ? (
                                  <p className="text-xs text-ink-3">No tags yet. Add one below — reusable across all forms, quizzes &amp; buttons.</p>
                                ) : (
                                  <div className="flex flex-wrap gap-1.5">
                                    {cfTags.map((tag) => {
                                      const isSelected = action.tagIds.includes(String(tag.id));
                                      return (
                                        <button
                                          key={tag.id}
                                          onClick={() => {
                                            const newTagIds = isSelected
                                              ? action.tagIds.filter(t => t !== String(tag.id))
                                              : [...action.tagIds, String(tag.id)];
                                            updateAction(action.id, { tagIds: newTagIds });
                                          }}
                                          className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                                            isSelected
                                              ? 'bg-accent-soft border-accent text-accent-ink'
                                              : 'bg-surface border-line text-ink-3 hover:border-ink-3'
                                          }`}
                                        >
                                          {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                                          {tag.name}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                                {!loadingTags && editConfig?.connectionId && (
                                  <TagCreator
                                    connectionId={editConfig.connectionId}
                                    onCreated={(tag) => {
                                      setCfTags((prev) => (prev.some((t) => t.id === tag.id) ? prev : [...prev, tag]));
                                      if (!action.tagIds.includes(String(tag.id))) {
                                        updateAction(action.id, { tagIds: [...action.tagIds, String(tag.id)] });
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Embed Script */}
                    {editConfig.status === 'published' && editConfig.buttonActions.length > 0 && (
                      <div className="mb-6">
                        <h3 className="font-display text-sm font-semibold text-ink-2 mb-2">Embed Script</h3>
                        <p className="text-xs text-ink-3 mb-2">Add this script to your ClickFunnels page to enable button tracking.</p>
                        <div className="relative">
                          <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3.5 font-mono text-[12px] leading-relaxed overflow-auto">
                            {buttonsApi.getScriptCode(editConfig.slug)}
                          </pre>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(buttonsApi.getScriptCode(editConfig.slug));
                              setCopiedScript(true);
                              setTimeout(() => setCopiedScript(false), 2000);
                            }}
                            className="absolute top-2 right-2 p-1.5 bg-surface-2 text-ink-3 hover:text-ink rounded"
                          >
                            {copiedScript ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    )}

                    {editConfig.status !== 'published' && editConfig.buttonActions.length > 0 && (
                      <div className="mb-6 p-3 bg-warning-soft border border-line rounded-lg text-warning text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Publish this config to get the button tracking embed script.
                      </div>
                    )}

                    {/* Click Log */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-sm font-semibold text-ink-2">Recent Clicks</h3>
                        <button
                          onClick={() => loadClicks(editConfig.id)}
                          className="text-xs text-accent-ink hover:underline"
                        >
                          Refresh
                        </button>
                      </div>
                      {loadingClicks ? (
                        <div className="flex justify-center py-6">
                          <Spinner className="h-5 w-5" />
                        </div>
                      ) : clicks.length === 0 ? (
                        <div className="text-center py-6 border border-dashed border-line rounded-lg">
                          <p className="text-ink-3 text-sm">No clicks recorded yet.</p>
                        </div>
                      ) : (
                        <div className="ds-card border-line overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-surface-2">
                              <tr>
                                <th className="text-left px-3 py-2 font-bold text-[10.5px] uppercase tracking-wide text-ink-3">Email</th>
                                <th className="text-left px-3 py-2 font-bold text-[10.5px] uppercase tracking-wide text-ink-3">Action</th>
                                <th className="text-left px-3 py-2 font-bold text-[10.5px] uppercase tracking-wide text-ink-3 hidden sm:table-cell">Tags</th>
                                <th className="text-left px-3 py-2 font-bold text-[10.5px] uppercase tracking-wide text-ink-3">Status</th>
                                <th className="text-right px-3 py-2 font-bold text-[10.5px] uppercase tracking-wide text-ink-3">When</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clicks.map((click) => (
                                <tr key={click.id} className="border-t border-line-2 hover:bg-surface-2 transition-colors">
                                  <td className="px-3 py-2 text-ink truncate max-w-[180px]">{click.email}</td>
                                  <td className="px-3 py-2 text-ink-2 font-mono text-xs">#{click.actionName}</td>
                                  <td className="px-3 py-2 text-ink-3 hidden sm:table-cell">{click.tagsApplied}</td>
                                  <td className="px-3 py-2">
                                    <Badge tone={click.status === 'success' ? 'success' : 'danger'}>{click.status}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-ink-3 text-xs text-right whitespace-nowrap">{timeAgo(click.createdAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Save */}
                    <div className="flex justify-end">
                      <Button onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card shadow-card p-4 md:p-6 w-full max-w-md">
              <h2 className="font-display text-lg font-semibold mb-4 text-ink">New Button Config</h2>
              <div className="space-y-4">
                <div>
                  <label className="ds-label">Name</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. Sales Page Buttons"
                    className="ds-input w-full"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="ds-label">ClickFunnels Connection</label>
                  {connections.length === 0 ? (
                    <p className="text-sm text-warning">No active ClickFunnels connections found. Add one in Vault &gt; Connections first.</p>
                  ) : (
                    <select
                      value={newConnId}
                      onChange={(e) => setNewConnId(e.target.value)}
                      className="ds-input w-full"
                    >
                      <option value="">Select connection...</option>
                      {connections.map((c) => (
                        <option key={c.id} value={c.id}>{c.displayName || c.workspaceName || c.platform}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="ghost"
                  onClick={() => { setShowCreateModal(false); setNewName(''); setNewConnId(''); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating || !newName.trim() || !newConnId}
                >
                  {creating ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
